/**
 * Wishlist - localStorage-backed product wishlist
 * Global API: window.Wishlist
 *
 * Events dispatched on `document`:
 *   - wishlist:change  { handles: string[], count: number }
 *   - wishlist:add     { handle: string }
 *   - wishlist:remove  { handle: string }
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'shopify_wishlist';
  var SHARE_PARAM = 'wishlist';

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(function (h) { return typeof h === 'string' && h.length; }) : [];
    } catch (e) {
      return [];
    }
  }

  function write(arr) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch (e) {
      console.warn('[wishlist] storage write failed', e);
    }
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  function emitChange() {
    var handles = read();
    emit('wishlist:change', { handles: handles, count: handles.length });
  }

  var Wishlist = {
    get: function () {
      return read();
    },

    has: function (handle) {
      if (!handle) return false;
      return read().indexOf(handle) !== -1;
    },

    add: function (handle) {
      if (!handle) return false;
      var list = read();
      if (list.indexOf(handle) !== -1) return false;
      list.push(handle);
      write(list);
      emit('wishlist:add', { handle: handle });
      emitChange();
      return true;
    },

    remove: function (handle) {
      if (!handle) return false;
      var list = read();
      var idx = list.indexOf(handle);
      if (idx === -1) return false;
      list.splice(idx, 1);
      write(list);
      emit('wishlist:remove', { handle: handle });
      emitChange();
      return true;
    },

    toggle: function (handle) {
      return this.has(handle) ? (this.remove(handle), false) : (this.add(handle), true);
    },

    clear: function () {
      write([]);
      emitChange();
    },

    count: function () {
      return read().length;
    },

    /**
     * Encode current wishlist into a shareable URL.
     * Uses comma-separated handles; URL-encoded.
     */
    shareUrl: function (baseUrl) {
      var handles = read();
      var base = baseUrl || (window.location.origin + '/pages/wishlist');
      if (!handles.length) return base;
      var sep = base.indexOf('?') === -1 ? '?' : '&';
      return base + sep + SHARE_PARAM + '=' + encodeURIComponent(handles.join(','));
    },

    /**
     * If a `?wishlist=handle1,handle2` param exists, return that list (without modifying storage).
     */
    readShared: function () {
      var params = new URLSearchParams(window.location.search);
      var raw = params.get(SHARE_PARAM);
      if (!raw) return null;
      return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    },

    /**
     * Merge a shared list into the user's own wishlist.
     */
    importShared: function () {
      var shared = this.readShared();
      if (!shared) return 0;
      var list = read();
      var added = 0;
      shared.forEach(function (h) {
        if (list.indexOf(h) === -1) {
          list.push(h);
          added++;
        }
      });
      if (added) {
        write(list);
        emitChange();
      }
      return added;
    }
  };

  window.Wishlist = Wishlist;

  /* ---------------- Custom elements ---------------- */

  /**
   * <wishlist-toggle product-handle="my-product"> ... </wishlist-toggle>
   * Toggles the product in/out of the wishlist. Reflects active state via [aria-pressed].
   */
  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      this.handle = this.getAttribute('product-handle');
      if (!this.handle) return;

      this._button = this.querySelector('button') || this;
      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);

      this._button.addEventListener('click', this._onClick);
      document.addEventListener('wishlist:change', this._onChange);

      this._sync();
    }

    disconnectedCallback() {
      if (this._button) this._button.removeEventListener('click', this._onClick);
      document.removeEventListener('wishlist:change', this._onChange);
    }

    _onClick(e) {
      e.preventDefault();
      e.stopPropagation();
      Wishlist.toggle(this.handle);
    }

    _onChange() {
      this._sync();
    }

    _sync() {
      var active = Wishlist.has(this.handle);
      this._button.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.classList.toggle('is-active', active);
      var labelOn = this.getAttribute('label-on');
      var labelOff = this.getAttribute('label-off');
      if (labelOn && labelOff) {
        this._button.setAttribute('aria-label', active ? labelOn : labelOff);
      }
    }
  }
  customElements.define('wishlist-toggle', WishlistToggle);

  /**
   * <wishlist-count>0</wishlist-count>
   * Renders the current count. Hides itself when count is 0 if [hide-when-empty] is set.
   */
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this._onChange = this._onChange.bind(this);
      document.addEventListener('wishlist:change', this._onChange);
      this._sync();
    }
    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this._onChange);
    }
    _onChange() { this._sync(); }
    _sync() {
      var n = Wishlist.count();
      this.textContent = String(n);
      if (this.hasAttribute('hide-when-empty')) {
        this.hidden = n === 0;
      }
    }
  }
  customElements.define('wishlist-count', WishlistCount);

  /* ---------------- Boot ---------------- */
  // Fire an initial change event so any listeners can paint correct state on load.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', emitChange);
  } else {
    emitChange();
  }
})();